/**
 * Mega Menu Components
 *
 * A set of reusable, composable components for building mega-menu navigation.
 * These components are headless/unstyled primitives that handle the interaction
 * logic (dropdowns, mobile menus, keyboard navigation) while allowing apps
 * to configure their own navigation items and routes.
 *
 * @example
 * ```gts
 * import { MegaMenu } from 'tio-reimagined/components/mega-menu';
 *
 * const NAV_ITEMS = [
 *   { id: 'home', icon: PhHouse, label: 'Home', route: 'authenticated.home' },
 *   { id: 'settings', icon: PhGear, label: 'Settings', dropdown: [...] },
 * ];
 *
 * <MegaMenu @items={{NAV_ITEMS}} @isFullscreen={{false}} />
 * ```
 */

import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { LinkTo } from '@ember/routing';
import { eq } from 'tio-ui/utilities';
import { PhCaretDown, PhCaretRight, PhList, PhX } from 'ember-phosphor-icons';
import type { ComponentLike } from '@glint/template';

/**
 * Modifier that positions a flyout panel based on available viewport space.
 * If there's not enough room on the right, it positions to the left.
 */
const positionFlyout = modifier((element: HTMLElement) => {
  const parent = element.parentElement;
  if (!parent) return;

  const parentRect = parent.getBoundingClientRect();
  const flyoutWidth = element.offsetWidth;
  const viewportWidth = window.innerWidth;

  // Check if flyout would overflow on the right
  const spaceOnRight = viewportWidth - parentRect.right;
  const spaceOnLeft = parentRect.left;

  if (spaceOnRight < flyoutWidth && spaceOnLeft > spaceOnRight) {
    // Position to the left
    element.style.left = 'auto';
    element.style.right = '100%';
    element.style.marginLeft = '0';
    element.style.marginRight = '-0.5rem'; // -ml-2 equivalent
  } else {
    // Position to the right (default)
    element.style.left = '100%';
    element.style.right = 'auto';
    element.style.marginLeft = '-0.5rem';
    element.style.marginRight = '0';
  }
});

/**
 * Configuration for a flyout submenu item (leaf node)
 */
export interface MegaMenuFlyoutItem {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
}

/**
 * Configuration for a dropdown menu item with a direct route
 */
export interface MegaMenuDropdownItemWithRoute {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** Ember route to navigate to */
  route: string;
  /** Optional stat/badge to display (e.g., "$125/mo", "84/120") */
  stat?: string;
  /** No flyout for route items */
  flyout?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item with a flyout submenu
 */
export interface MegaMenuDropdownItemWithFlyout {
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Short description shown below label */
  description: string;
  /** No direct route for flyout items */
  route?: never;
  /** Optional stat/badge to display */
  stat?: string;
  /** Nested flyout submenu */
  flyout: MegaMenuFlyoutItem[];
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Configuration for a dropdown menu item (either route or flyout)
 */
export type MegaMenuDropdownItem = MegaMenuDropdownItemWithRoute | MegaMenuDropdownItemWithFlyout;

/**
 * Navigation item with a direct route (no dropdown)
 */
export interface MegaMenuItemWithRoute {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Ember route to navigate to */
  route: string;
  /** Never has a dropdown */
  dropdown?: never;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

/**
 * Navigation item with a dropdown menu
 */
export interface MegaMenuItemWithDropdown {
  /** Unique identifier for the nav item */
  id: string;
  /** Phosphor icon component */
  icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
  /** Display label */
  label: string;
  /** Never has a direct route */
  route?: never;
  /** Dropdown menu items */
  dropdown: MegaMenuDropdownItem[];
  /** Optional route for the "View Dashboard" CTA (overrides auto-detection) */
  indexRoute?: string;
  /** Optional visibility predicate - return false to hide */
  isVisible?: boolean;
}

export type MegaMenuItem = MegaMenuItemWithRoute | MegaMenuItemWithDropdown;

export interface MegaMenuSignature {
  Args: {
    /** Navigation items configuration */
    items: MegaMenuItem[];
    /** Whether layout is fullscreen (affects container width) */
    isFullscreen?: boolean;
    /** Optional logo/brand component to render */
    logo?: ComponentLike<{ Element: HTMLElement }>;
    /** Label for the mobile brand display */
    brandLabel?: string;
  };
  Element: HTMLElement;
}

/**
 * Helper to get the first dropdown route - used for "View Dashboard" CTA
 */
const getFirstDropdownRoute = (item: MegaMenuItem): string => {
  // Use explicit index route if provided
  if ('indexRoute' in item && item.indexRoute) {
    return item.indexRoute;
  }
  if (item.dropdown && item.dropdown.length > 0) {
    // Find the first item that has a direct route (not a flyout)
    const firstWithRoute = item.dropdown.find((d) => d.route);
    if (firstWithRoute?.route) {
      return firstWithRoute.route;
    }
    // If all have flyouts, get the first flyout item's route
    const firstWithFlyout = item.dropdown.find((d) => d.flyout && d.flyout.length > 0);
    if (firstWithFlyout?.flyout?.[0]?.route) {
      return firstWithFlyout.flyout[0].route;
    }
  }
  return '';
};

/**
 * Helper to filter visible dropdown items
 */
const getVisibleDropdownItems = (items: MegaMenuDropdownItem[]): MegaMenuDropdownItem[] => {
  return items.filter((item) => item.isVisible !== false);
};

/**
 * MegaMenu - Full mega-menu navigation component
 *
 * Features:
 * - Desktop: Horizontal nav with mega-menu dropdowns
 * - Mobile: Hamburger menu with slide-out panel and accordions
 * - Full keyboard navigation (arrow keys, Home/End, Escape)
 * - Dark mode support
 */
export class MegaMenu extends Component<MegaMenuSignature> {
  @tracked openDropdownId: string | null = null;
  @tracked isMobileMenuOpen = false;
  @tracked mobileExpandedId: string | null = null;
  @tracked mobileExpandedFlyout: string | null = null;
  @tracked openFlyoutLabel: string | null = null;
  @tracked focusedMenuIndex = -1;
  @tracked focusedFlyoutIndex = -1;

  @cached
  get visibleNavItems() {
    return this.args.items.filter((item) => item.isVisible !== false);
  }

  get brandLabel() {
    return this.args.brandLabel ?? 'TIO';
  }

  toggleDropdown = (id: string, event?: MouseEvent) => {
    if (this.openDropdownId === id) {
      this.openDropdownId = null;
    } else {
      this.openDropdownId = id;
    }
    // Safari doesn't focus <button> on click. Re-focus the trigger after
    // Glimmer re-renders so keyboard events (ArrowDown, Escape) fire on it.
    if (event) {
      const btn = event.currentTarget as HTMLElement;
      requestAnimationFrame(() => btn?.focus());
    }
  };

  closeDropdown = () => {
    this.openDropdownId = null;
  };

  // Deferred close for links - prevents interference with LinkTo navigation
  handleLinkClick = () => {
    setTimeout(() => {
      this.openDropdownId = null;
      this.isMobileMenuOpen = false;
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
      this.openFlyoutLabel = null;
    }, 0);
  };

  // Flyout hover handlers - container-based, no debounce needed
  openFlyout = (label: string) => {
    this.openFlyoutLabel = label;
  };

  closeFlyout = () => {
    this.openFlyoutLabel = null;
  };

  toggleMobileMenu = () => {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
    if (!this.isMobileMenuOpen) {
      this.mobileExpandedId = null;
      this.mobileExpandedFlyout = null;
    }
  };

  closeMobileMenu = () => {
    this.isMobileMenuOpen = false;
    this.mobileExpandedId = null;
    this.mobileExpandedFlyout = null;
  };

  toggleMobileExpanded = (id: string) => {
    if (this.mobileExpandedId === id) {
      this.mobileExpandedId = null;
    } else {
      this.mobileExpandedId = id;
    }
    this.mobileExpandedFlyout = null;
  };

  toggleMobileFlyout = (label: string) => {
    if (this.mobileExpandedFlyout === label) {
      this.mobileExpandedFlyout = null;
    } else {
      this.mobileExpandedFlyout = label;
    }
  };

  // Get the current open dropdown's visible items
  get currentDropdownItems() {
    const openItem = this.visibleNavItems.find((item) => item.id === this.openDropdownId);
    const items = openItem?.dropdown ?? [];
    return getVisibleDropdownItems(items);
  }

  // Get the current open flyout's items
  get currentFlyoutItems() {
    if (!this.openFlyoutLabel) return [];
    const flyoutItem = this.currentDropdownItems.find(
      (item) => item.label === this.openFlyoutLabel && item.flyout
    );
    return flyoutItem?.flyout ?? [];
  }

  // Focus a menu item by index using DOM query
  focusMenuItem = (index: number) => {
    const itemCount = this.currentDropdownItems.length;
    if (index >= 0 && index < itemCount) {
      this.focusedMenuIndex = index;
      this.focusedFlyoutIndex = -1;
      // Query for the menu items in the DOM
      requestAnimationFrame(() => {
        const menuItems = document.querySelectorAll(
          '[role="menu"] > div > [role="menuitem"], [role="menu"] > div > div > [role="menuitem"]'
        );
        const item = menuItems[index] as HTMLElement;
        item?.focus();
      });
    }
  };

  // Focus a flyout item by index using DOM query
  focusFlyoutItem = (index: number) => {
    const flyoutCount = this.currentFlyoutItems.length;
    if (index >= 0 && index < flyoutCount) {
      this.focusedFlyoutIndex = index;
      // Query for flyout menu items specifically (in the nested role="menu")
      requestAnimationFrame(() => {
        const flyoutMenus = document.querySelectorAll('[role="menu"] [role="menu"]');
        const flyoutMenu = flyoutMenus[flyoutMenus.length - 1]; // Get the last (most nested) menu
        if (flyoutMenu) {
          const flyoutItems = flyoutMenu.querySelectorAll('[role="menuitem"]');
          const item = flyoutItems[index] as HTMLElement;
          item?.focus();
        }
      });
    }
  };

  // Handle keyboard navigation on dropdown trigger
  handleTriggerKeyDown = (id: string, event: KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        this.closeDropdown();
        // Focus the trigger button
        {
          const trigger = (event.target as HTMLElement).closest('button');
          trigger?.focus();
        }
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.toggleDropdown(id);
        // Focus first menu item when opening
        if (this.openDropdownId === id) {
          this.focusedMenuIndex = -1;
          this.focusedFlyoutIndex = -1;
          // Wait for render then focus first item
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            this.focusMenuItem(0);
          });
        } else {
          this.focusMenuItem(0);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.openDropdownId !== id) {
          this.toggleDropdown(id);
          requestAnimationFrame(() => {
            const lastIndex = this.currentDropdownItems.length - 1;
            this.focusMenuItem(lastIndex);
          });
        } else {
          const lastIndex = this.currentDropdownItems.length - 1;
          this.focusMenuItem(lastIndex);
        }
        break;
    }
  };

  // Handle keyboard navigation within dropdown menu
  handleMenuKeyDown = (
    index: number,
    hasFlyout: boolean,
    flyoutLabel: string,
    event: KeyboardEvent
  ) => {
    const itemCount = this.currentDropdownItems.length;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closeFlyout();
        this.closeDropdown();
        // Focus the trigger button
        const trigger = document.querySelector('button[aria-expanded="true"]') as HTMLElement;
        trigger?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          // Navigate within flyout
          const flyoutCount = this.currentFlyoutItems.length;
          const nextFlyoutIndex = (this.focusedFlyoutIndex + 1) % flyoutCount;
          this.focusFlyoutItem(nextFlyoutIndex);
        } else {
          // Navigate to next menu item
          const nextIndex = (index + 1) % itemCount;
          this.closeFlyout();
          this.focusMenuItem(nextIndex);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          // Navigate within flyout
          const flyoutCount = this.currentFlyoutItems.length;
          const prevFlyoutIndex =
            this.focusedFlyoutIndex <= 0 ? flyoutCount - 1 : this.focusedFlyoutIndex - 1;
          this.focusFlyoutItem(prevFlyoutIndex);
        } else {
          // Navigate to previous menu item
          const prevIndex = index <= 0 ? itemCount - 1 : index - 1;
          this.closeFlyout();
          this.focusMenuItem(prevIndex);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (hasFlyout) {
          // Open flyout and focus first item
          this.openFlyout(flyoutLabel);
          requestAnimationFrame(() => {
            this.focusFlyoutItem(0);
          });
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (this.openFlyoutLabel) {
          // Close flyout and return to menu item
          this.closeFlyout();
          this.focusedFlyoutIndex = -1;
          this.focusMenuItem(index);
        }
        break;
      case 'Home':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          this.focusFlyoutItem(0);
        } else {
          this.focusMenuItem(0);
        }
        break;
      case 'End':
        event.preventDefault();
        if (this.focusedFlyoutIndex >= 0 && this.openFlyoutLabel) {
          const flyoutCount = this.currentFlyoutItems.length;
          this.focusFlyoutItem(flyoutCount - 1);
        } else {
          this.focusMenuItem(itemCount - 1);
        }
        break;
      case 'Tab':
        // Allow tab to close menu and move to next element
        this.closeFlyout();
        this.closeDropdown();
        break;
      case 'Enter':
      case ' ':
        if (hasFlyout && this.focusedFlyoutIndex < 0) {
          // Open flyout when on a flyout trigger
          event.preventDefault();
          this.openFlyout(flyoutLabel);
          requestAnimationFrame(() => {
            this.focusFlyoutItem(0);
          });
        }
        // Otherwise let the default behavior (link navigation) happen
        break;
    }
  };

  // Handle keyboard navigation within flyout
  handleFlyoutKeyDown = (menuIndex: number, flyoutIndex: number, event: KeyboardEvent) => {
    const flyoutCount = this.currentFlyoutItems.length;

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.closeFlyout();
        this.focusedFlyoutIndex = -1;
        this.focusMenuItem(menuIndex);
        break;
      case 'ArrowDown':
        event.preventDefault();
        {
          const nextIndex = (flyoutIndex + 1) % flyoutCount;
          this.focusFlyoutItem(nextIndex);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        {
          const prevIndex = flyoutIndex <= 0 ? flyoutCount - 1 : flyoutIndex - 1;
          this.focusFlyoutItem(prevIndex);
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.closeFlyout();
        this.focusedFlyoutIndex = -1;
        this.focusMenuItem(menuIndex);
        break;
      case 'Home':
        event.preventDefault();
        this.focusFlyoutItem(0);
        break;
      case 'End':
        event.preventDefault();
        this.focusFlyoutItem(flyoutCount - 1);
        break;
      case 'Tab':
        this.closeFlyout();
        this.closeDropdown();
        break;
    }
  };

  <template>
    {{! Desktop Navigation }}
    <nav
      class="hidden md:block border-b bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 sticky top-0 z-30"
      role="navigation"
      aria-label="Main navigation"
      ...attributes
    >
      <div class="{{if @isFullscreen 'px-6' 'max-w-7xl mx-auto px-6'}}">
        <div class="flex items-center gap-1">
          {{#each this.visibleNavItems as |item|}}
            {{#if item.dropdown}}
              {{! Dropdown nav item }}
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center gap-2 px-4 py-4 text-sm font-medium transition-colors
                    {{if
                      (eq this.openDropdownId item.id)
                      'text-violet-600 dark:text-violet-400 bg-violet-50 dark:bg-violet-900/30'
                      'text-gray-700 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-gray-50'
                    }}"
                  aria-expanded="{{if (eq this.openDropdownId item.id) 'true' 'false'}}"
                  aria-haspopup="menu"
                  {{on "click" (fn this.toggleDropdown item.id)}}
                  {{on "keydown" (fn this.handleTriggerKeyDown item.id)}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{18}} />
                  {{/let}}
                  <span>{{item.label}}</span>
                  <PhCaretDown
                    @size={{14}}
                    class="transition-transform
                      {{if (eq this.openDropdownId item.id) 'rotate-180'}}"
                  />
                </button>

                {{#if (eq this.openDropdownId item.id)}}
                  {{! Backdrop }}
                  <button
                    type="button"
                    class="fixed inset-0 z-10"
                    aria-label="Close menu"
                    tabindex="-1"
                    {{on "click" this.closeDropdown}}
                  ></button>

                  {{! Mega-menu dropdown }}
                  <div
                    class="absolute left-0 mt-0 w-80 bg-white dark:bg-gray-900 rounded-b-lg shadow-xl border border-gray-200 dark:border-gray-700 border-t-0 z-20 animate-in fade-in slide-in-from-top-2 duration-200"
                    role="menu"
                  >
                    <div class="p-2" role="group">
                      {{#each (getVisibleDropdownItems item.dropdown) as |dropdownItem menuIndex|}}
                        {{#if dropdownItem.flyout}}
                          {{! Flyout submenu - uses relative positioning with hover on container }}
                          {{! The entire container (trigger + flyout) shares the same hover zone }}
                          <div
                            class="relative"
                            role="none"
                            {{on "mouseenter" (fn this.openFlyout dropdownItem.label)}}
                            {{on "mouseleave" this.closeFlyout}}
                          >
                            <button
                              type="button"
                              class="w-full flex items-start gap-3 p-3 rounded-lg transition-colors group
                                {{if
                                  (eq this.openFlyoutLabel dropdownItem.label)
                                  'bg-violet-50 dark:bg-violet-900/50'
                                  'hover:bg-gray-100'
                                }}"
                              role="menuitem"
                              tabindex={{if (eq this.focusedMenuIndex menuIndex) "0" "-1"}}
                              aria-haspopup="menu"
                              aria-expanded={{if
                                (eq this.openFlyoutLabel dropdownItem.label)
                                "true"
                                "false"
                              }}
                              {{on
                                "keydown"
                                (fn this.handleMenuKeyDown menuIndex true dropdownItem.label)
                              }}
                            >
                              <div
                                class="p-2 rounded-lg bg-gray-100 text-gray-600 group-hover:bg-violet-100 dark:group-hover:bg-violet-900/50 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors"
                              >
                                {{#let dropdownItem.icon as |DropdownIcon|}}
                                  <DropdownIcon @size={{20}} />
                                {{/let}}
                              </div>
                              <div class="flex-1 min-w-0 text-left">
                                <div class="flex items-center gap-2">
                                  <span
                                    class="font-medium text-gray-900 dark:text-white group-hover:text-violet-900 dark:group-hover:text-violet-300"
                                  >{{dropdownItem.label}}</span>
                                </div>
                                <span
                                  class="text-sm text-gray-500 mt-0.5 block"
                                >{{dropdownItem.description}}</span>
                              </div>
                              <PhCaretRight @size={{16}} class="text-gray-400 mt-2" />
                            </button>

                            {{! Flyout panel - positioned based on viewport space }}
                            {{#if (eq this.openFlyoutLabel dropdownItem.label)}}
                              <div
                                class="absolute top-0 w-72 bg-white dark:bg-gray-900 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 animate-in fade-in duration-150 z-30"
                                {{positionFlyout}}
                              >
                                <div class="p-2" role="menu">
                                  {{#each dropdownItem.flyout as |flyoutItem flyoutIndex|}}
                                    <LinkTo
                                      @route={{flyoutItem.route}}
                                      class="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors group"
                                      role="menuitem"
                                      tabindex={{if
                                        (eq this.focusedFlyoutIndex flyoutIndex)
                                        "0"
                                        "-1"
                                      }}
                                      {{on "click" this.handleLinkClick}}
                                      {{on
                                        "keydown"
                                        (fn this.handleFlyoutKeyDown menuIndex flyoutIndex)
                                      }}
                                    >
                                      <div
                                        class="p-2 rounded-lg bg-gray-100 text-gray-600 group-hover:bg-violet-100 dark:group-hover:bg-violet-900/50 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors"
                                      >
                                        {{#let flyoutItem.icon as |FlyoutIcon|}}
                                          <FlyoutIcon @size={{18}} />
                                        {{/let}}
                                      </div>
                                      <div class="flex-1 min-w-0">
                                        <span
                                          class="font-medium text-gray-900 dark:text-white group-hover:text-violet-900 dark:group-hover:text-violet-300"
                                        >{{flyoutItem.label}}</span>
                                        <span
                                          class="text-sm text-gray-500 mt-0.5 block"
                                        >{{flyoutItem.description}}</span>
                                      </div>
                                    </LinkTo>
                                  {{/each}}
                                </div>
                              </div>
                            {{/if}}
                          </div>
                        {{else}}
                          {{! Regular dropdown link }}
                          <LinkTo
                            @route={{dropdownItem.route}}
                            class="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors group"
                            role="menuitem"
                            tabindex={{if (eq this.focusedMenuIndex menuIndex) "0" "-1"}}
                            {{on "click" this.handleLinkClick}}
                            {{on "keydown" (fn this.handleMenuKeyDown menuIndex false "")}}
                          >
                            <div
                              class="p-2 rounded-lg bg-gray-100 text-gray-600 group-hover:bg-violet-100 dark:group-hover:bg-violet-900/50 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors"
                            >
                              {{#let dropdownItem.icon as |DropdownIcon|}}
                                <DropdownIcon @size={{20}} />
                              {{/let}}
                            </div>
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center gap-2">
                                <span
                                  class="font-medium text-gray-900 dark:text-white group-hover:text-violet-900 dark:group-hover:text-violet-300"
                                >{{dropdownItem.label}}</span>
                                {{#if dropdownItem.stat}}
                                  <span
                                    class="text-xs font-medium px-2 py-0.5 bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded-full"
                                  >{{dropdownItem.stat}}</span>
                                {{/if}}
                              </div>
                              <p
                                class="text-sm text-gray-500 mt-0.5"
                              >{{dropdownItem.description}}</p>
                            </div>
                          </LinkTo>
                        {{/if}}
                      {{/each}}
                    </div>

                    {{! CTA at bottom }}
                    <div class="border-t border-gray-100 p-3 bg-gray-50 dark:bg-gray-800/50">
                      <LinkTo
                        @route={{getFirstDropdownRoute item}}
                        class="flex items-center justify-center gap-2 w-full py-2 px-4 text-sm font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 dark:hover:text-violet-300 hover:bg-violet-100 dark:hover:bg-violet-900/30 rounded-lg transition-colors"
                        {{on "click" this.handleLinkClick}}
                      >
                        View
                        {{item.label}}
                        Dashboard →
                      </LinkTo>
                    </div>
                  </div>
                {{/if}}
              </div>
            {{else}}
              {{! Simple nav item }}
              <LinkTo
                @route={{item.route}}
                class="flex items-center gap-2 px-4 py-4 text-sm font-medium transition-colors text-gray-700 hover:text-violet-600 dark:hover:text-violet-400 hover:bg-gray-50"
              >
                {{#let item.icon as |Icon|}}
                  <Icon @size={{18}} />
                {{/let}}
                <span>{{item.label}}</span>
              </LinkTo>
            {{/if}}
          {{/each}}
        </div>
      </div>
    </nav>

    {{! Mobile Navigation }}
    <nav
      class="md:hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-30"
      role="navigation"
      aria-label="Mobile navigation"
    >
      <div class="flex items-center justify-between px-4 py-3">
        <div class="flex items-center gap-2">
          {{#if @logo}}
            {{#let @logo as |Logo|}}
              <Logo />
            {{/let}}
          {{/if}}
          <span class="font-bold text-gray-900 dark:text-white">{{this.brandLabel}}</span>
        </div>

        <button
          type="button"
          class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          aria-label="{{if this.isMobileMenuOpen 'Close menu' 'Open menu'}}"
          aria-expanded="{{if this.isMobileMenuOpen 'true' 'false'}}"
          {{on "click" this.toggleMobileMenu}}
        >
          {{#if this.isMobileMenuOpen}}
            <PhX @size={{24}} />
          {{else}}
            <PhList @size={{24}} />
          {{/if}}
        </button>
      </div>

      {{#if this.isMobileMenuOpen}}
        {{! Mobile menu overlay }}
        <button
          type="button"
          class="fixed inset-0 bg-black/50 z-40"
          aria-label="Close menu"
          {{on "click" this.closeMobileMenu}}
        ></button>

        {{! Mobile menu panel }}
        <div
          class="fixed top-0 right-0 bottom-0 w-80 max-w-[calc(100vw-48px)] bg-white dark:bg-gray-900 z-50 shadow-xl overflow-y-auto animate-in slide-in-from-right duration-300"
        >
          <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <span class="font-bold text-gray-900 dark:text-white">Menu</span>
            <button
              type="button"
              class="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
              {{on "click" this.closeMobileMenu}}
            >
              <PhX @size={{20}} />
            </button>
          </div>

          <div class="py-2">
            {{#each this.visibleNavItems as |item|}}
              {{#if item.dropdown}}
                {{! Accordion nav item }}
                <div class="border-b border-gray-100">
                  <button
                    type="button"
                    class="flex items-center justify-between w-full px-4 py-3 text-left"
                    aria-expanded="{{if (eq this.mobileExpandedId item.id) 'true' 'false'}}"
                    {{on "click" (fn this.toggleMobileExpanded item.id)}}
                  >
                    <div class="flex items-center gap-3">
                      {{#let item.icon as |Icon|}}
                        <Icon @size={{20}} class="text-gray-500" />
                      {{/let}}
                      <span class="font-medium text-gray-900 dark:text-white">{{item.label}}</span>
                    </div>
                    <PhCaretDown
                      @size={{16}}
                      class="text-gray-400 transition-transform
                        {{if (eq this.mobileExpandedId item.id) 'rotate-180'}}"
                    />
                  </button>

                  {{#if (eq this.mobileExpandedId item.id)}}
                    <div class="bg-gray-50 dark:bg-gray-800/50 pb-2">
                      {{#each (getVisibleDropdownItems item.dropdown) as |dropdownItem|}}
                        {{#if dropdownItem.flyout}}
                          {{! Nested accordion for flyout }}
                          <div>
                            <button
                              type="button"
                              class="flex items-center justify-between w-full px-4 py-3 pl-12 text-left hover:bg-gray-100 transition-colors"
                              aria-expanded="{{if
                                (eq this.mobileExpandedFlyout dropdownItem.label)
                                'true'
                                'false'
                              }}"
                              {{on "click" (fn this.toggleMobileFlyout dropdownItem.label)}}
                            >
                              <div class="flex items-center gap-3">
                                {{#let dropdownItem.icon as |DropdownIcon|}}
                                  <DropdownIcon @size={{18}} class="text-gray-400" />
                                {{/let}}
                                <span
                                  class="text-sm font-medium text-gray-700"
                                >{{dropdownItem.label}}</span>
                              </div>
                              <PhCaretDown
                                @size={{14}}
                                class="text-gray-400 transition-transform
                                  {{if
                                    (eq this.mobileExpandedFlyout dropdownItem.label)
                                    'rotate-180'
                                  }}"
                              />
                            </button>

                            {{#if (eq this.mobileExpandedFlyout dropdownItem.label)}}
                              <div class="bg-gray-100 dark:bg-gray-700/50 pb-1">
                                {{#each dropdownItem.flyout as |flyoutItem|}}
                                  <LinkTo
                                    @route={{flyoutItem.route}}
                                    class="flex items-center gap-3 px-4 py-2.5 pl-20 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    {{on "click" this.handleLinkClick}}
                                  >
                                    {{#let flyoutItem.icon as |FlyoutIcon|}}
                                      <FlyoutIcon @size={{16}} class="text-gray-400" />
                                    {{/let}}
                                    <span class="text-sm text-gray-600">{{flyoutItem.label}}</span>
                                  </LinkTo>
                                {{/each}}
                              </div>
                            {{/if}}
                          </div>
                        {{else}}
                          {{! Regular dropdown link }}
                          <LinkTo
                            @route={{dropdownItem.route}}
                            class="flex items-center gap-3 px-4 py-3 pl-12 hover:bg-gray-100 transition-colors"
                            {{on "click" this.handleLinkClick}}
                          >
                            {{#let dropdownItem.icon as |DropdownIcon|}}
                              <DropdownIcon @size={{18}} class="text-gray-400" />
                            {{/let}}
                            <div class="flex-1">
                              <div class="flex items-center gap-2">
                                <span
                                  class="text-sm font-medium text-gray-700"
                                >{{dropdownItem.label}}</span>
                                {{#if dropdownItem.stat}}
                                  <span
                                    class="text-xs px-1.5 py-0.5 bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded"
                                  >{{dropdownItem.stat}}</span>
                                {{/if}}
                              </div>
                            </div>
                          </LinkTo>
                        {{/if}}
                      {{/each}}
                    </div>
                  {{/if}}
                </div>
              {{else}}
                {{! Simple mobile nav item }}
                <LinkTo
                  @route={{item.route}}
                  class="flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  {{on "click" this.handleLinkClick}}
                >
                  {{#let item.icon as |Icon|}}
                    <Icon @size={{20}} class="text-gray-500" />
                  {{/let}}
                  <span class="font-medium text-gray-900 dark:text-white">{{item.label}}</span>
                </LinkTo>
              {{/if}}
            {{/each}}
          </div>
        </div>
      {{/if}}
    </nav>
  </template>
}

export default MegaMenu;
